import React, { useCallback, useEffect, useState } from 'react';
import { Container, Card, Button, Alert, Spinner, Row, Col, Badge, Tabs, Select, Input } from '../../../../core/ui';
import {
  listCategories,
  createCategory,
  deleteCategory,
  listRules,
  createRule,
  deleteRule,
} from '../../api/budget';
import { useTranslation } from '../../../../core/context/TranslationContext';
import styles from './CategoryManager.module.css';

const CategoryManager = () => {
  const { t, language } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('categories');

  const [catDraft, setCatDraft] = useState({
    key: '', displayNameTr: '', displayNameEn: '',
  });
  const [ruleDraft, setRuleDraft] = useState({
    matchType: 'CONTAINS', pattern: '', categoryId: '', priority: 50,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [c, r] = await Promise.all([listCategories(), listRules()]);
      setCategories(c);
      setRules(r);
    } catch (err) {
      setError(err?.message || t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const categoryLabel = (c) =>
    (language === 'tr' ? c.displayNameTr : c.displayNameEn) || c.key;

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await createCategory({ ...catDraft });
      setCatDraft({ key: '', displayNameTr: '', displayNameEn: '' });
      load();
    } catch (err) {
      setError(err?.message || t('error'));
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm(t('budget.confirmDeleteCategory'))) return;
    try {
      await deleteCategory(id);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('error'));
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await createRule({
        matchType: ruleDraft.matchType,
        pattern: ruleDraft.pattern,
        categoryId: Number(ruleDraft.categoryId),
        priority: Number(ruleDraft.priority),
      });
      setRuleDraft({ matchType: 'CONTAINS', pattern: '', categoryId: '', priority: 50 });
      load();
    } catch (err) {
      setError(err?.message || t('error'));
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      await deleteRule(id);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('error'));
    }
  };

  if (loading) return <Container><Spinner label={t('loading')} /></Container>;

  return (
    <Container>
      <h2 className={styles.heading}>{t('budget.categoriesAndRules')}</h2>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Tabs variant="pills" className={styles.tabs}>
        <Tabs.Tab active={activeTab === 'categories'} onClick={() => setActiveTab('categories')}>
          {t('budget.categories')}
        </Tabs.Tab>
        <Tabs.Tab active={activeTab === 'rules'} onClick={() => setActiveTab('rules')}>
          {t('budget.rules')}
        </Tabs.Tab>
      </Tabs>

      {activeTab === 'categories' && (
        <div className={styles.tabContent}>
          <Card className={styles.formCard}>
            <Card.Header>{t('budget.addCategory')}</Card.Header>
            <Card.Body>
              <form onSubmit={handleCreateCategory}>
                <Row gutter="sm">
                  <Col span={3}>
                    <Input
                      placeholder="key"
                      value={catDraft.key}
                      onChange={(e) => setCatDraft({ ...catDraft, key: e.target.value })}
                      required
                    />
                  </Col>
                  <Col span={3}>
                    <Input
                      placeholder={t('budget.displayNameTr')}
                      value={catDraft.displayNameTr}
                      onChange={(e) => setCatDraft({ ...catDraft, displayNameTr: e.target.value })}
                    />
                  </Col>
                  <Col span={3}>
                    <Input
                      placeholder={t('budget.displayNameEn')}
                      value={catDraft.displayNameEn}
                      onChange={(e) => setCatDraft({ ...catDraft, displayNameEn: e.target.value })}
                    />
                  </Col>
                  <Col span={3}>
                    <Button type="submit" className={styles.fullWidth}>{t('budget.add')}</Button>
                  </Col>
                </Row>
              </form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className={styles.badgeList}>
                {categories.map(c => (
                  <Badge
                    key={c.id}
                    variant={c.systemDefined ? 'neutral' : 'accent'}
                    className={styles.badgeItem}
                  >
                    <span>{categoryLabel(c)}</span>
                    {!c.systemDefined && (
                      <button
                        type="button"
                        className={styles.badgeClose}
                        aria-label="remove"
                        onClick={() => handleDeleteCategory(c.id)}
                      />
                    )}
                  </Badge>
                ))}
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      {activeTab === 'rules' && (
        <div className={styles.tabContent}>
          <Card className={styles.formCard}>
            <Card.Header>{t('budget.addRule')}</Card.Header>
            <Card.Body>
              <form onSubmit={handleCreateRule}>
                <Row gutter="sm">
                  <Col span={2}>
                    <Select
                      value={ruleDraft.matchType}
                      onChange={(e) => setRuleDraft({ ...ruleDraft, matchType: e.target.value })}
                    >
                      <option value="CONTAINS">CONTAINS</option>
                      <option value="EXACT">EXACT</option>
                      <option value="REGEX">REGEX</option>
                    </Select>
                  </Col>
                  <Col span={4}>
                    <Input
                      placeholder={t('budget.pattern')}
                      value={ruleDraft.pattern}
                      onChange={(e) => setRuleDraft({ ...ruleDraft, pattern: e.target.value })}
                      required
                    />
                  </Col>
                  <Col span={3}>
                    <Select
                      value={ruleDraft.categoryId}
                      onChange={(e) => setRuleDraft({ ...ruleDraft, categoryId: e.target.value })}
                      required
                    >
                      <option value="">{t('budget.pickCategory')}</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{categoryLabel(c)}</option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={2}>
                    <Input
                      type="number"
                      placeholder={t('budget.priority')}
                      value={ruleDraft.priority}
                      onChange={(e) => setRuleDraft({ ...ruleDraft, priority: e.target.value })}
                    />
                  </Col>
                  <Col span={1}>
                    <Button type="submit" className={styles.fullWidth}>{t('budget.add')}</Button>
                  </Col>
                </Row>
              </form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body className={styles.tableBody}>
              <table className={styles.rulesTable}>
                <thead>
                  <tr>
                    <th>{t('budget.matchType')}</th>
                    <th>{t('budget.pattern')}</th>
                    <th>{t('budget.category')}</th>
                    <th>{t('budget.priority')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map(r => {
                    const cat = categories.find(c => c.id === r.categoryId);
                    const isSystem = r.userId == null;
                    return (
                      <tr key={r.id}>
                        <td><Badge variant="accent">{r.matchType}</Badge></td>
                        <td><code>{r.pattern}</code></td>
                        <td>{cat ? categoryLabel(cat) : `#${r.categoryId}`}</td>
                        <td>{r.priority}</td>
                        <td>
                          {isSystem ? (
                            <Badge variant="neutral">{t('budget.system')}</Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="dangerGhost"
                              onClick={() => handleDeleteRule(r.id)}
                            >
                              {t('delete')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {rules.length === 0 && (
                    <tr><td colSpan={5} className={styles.emptyCell}>{t('noData')}</td></tr>
                  )}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default CategoryManager;
